import type { ArcgisLayerData } from '@atom/types/arcgis';

import { fetchServiceJson, resolveLayerUrl } from './arcgisClient';
import {
  fetchLayerGeoJson,
  offsetForZoom,
  queryObjectIds,
} from './arcgisFeatures';
import { rendererToSymbology } from './arcgisSymbology';
import type { ArcGISServiceJson } from './arcgisTypes';

export { detectService } from './detectService';
export type { ServiceType } from './arcgisTypes';

// ArcGIS layers load once and aren't refetched at higher zooms, so a layer
// fetched generalized looks blocky if you later zoom past its detail level. We
// only pay that cost for layers heavy enough to lag the map: at or below this
// feature count we fetch full-resolution geometry; above it we ask the server
// to generalize to GENERALIZE_ZOOM detail to keep the payload manageable.
const GENERALIZE_FEATURE_THRESHOLD = 20000;

// Zoom level whose ~1px tolerance the server generalizes heavy layers to —
// roughly metro-area detail, a compromise between fidelity and vertex count.
const GENERALIZE_ZOOM = 12;

/**
 * Build a `name → alias` map from a layer's field definitions so the popup can
 * show human-readable labels instead of raw field keys.
 */
const fieldAliasesFrom = (
  fields: ArcGISServiceJson['fields'],
): Record<string, string> => {
  if (!Array.isArray(fields)) {
    return {};
  }

  return fields.reduce<Record<string, string>>(
    (acc, field) =>
      field.alias && field.alias !== field.name
        ? { ...acc, [field.name]: field.alias }
        : acc,
    {},
  );
};

/**
 * Fetch a layer's geometry, symbology, and field aliases together.
 */
export const loadLayerData = async (url: string): Promise<ArcgisLayerData> => {
  const layerUrl = await resolveLayerUrl(url);

  const [metadata, objectIds] = await Promise.all([
    fetchServiceJson(layerUrl).catch(() => null),
    queryObjectIds(layerUrl),
  ]);

  const usable = metadata && !metadata.error ? metadata : null;

  const maxAllowableOffset =
    objectIds !== null && objectIds.length > GENERALIZE_FEATURE_THRESHOLD
      ? offsetForZoom(GENERALIZE_ZOOM)
      : undefined;

  const features = await fetchLayerGeoJson(layerUrl, objectIds, {
    maxRecordCount: usable?.maxRecordCount,
    maxAllowableOffset,
  });

  return {
    features,
    symbology: rendererToSymbology(usable?.drawingInfo?.renderer),
    fieldAliases: fieldAliasesFrom(usable?.fields),
  };
};
